import Backbone from 'backbone'
import context from './scorecards-context'
import template from './scorecards-overview.hbs'
import Performance from './performance'
import Historical from './historical'
import Values from './values'
import Related from 'scoreboard/related-items/related-items-list'
import Calculations from './calculations'
import tabs from '../scorecards-tabs.hbs'
import _ from 'lodash'
import globals from 'scoreboard/components/globals'
import NotesLayout from 'scoreboard/notes/notes-layout'
import ScorecardsDisplayHeader from 'scoreboard/scorecards/scorecards-display-header'
import SendToOptions from 'scoreboard/components/send-to-options'
import ImportOptions from 'scoreboard/components/import-options'
import visible from 'scoreboard/scorecards/visible'
import calendars from 'scoreboard/components/calendars'
import notVisibleMessage from 'scoreboard/scorecards/not-visible-message'
import cannotEdit from 'scoreboard/components/cannot-edit.hbs'
import layoutManager from 'scoreboard/components/layout-manager'
import conductor from 'scoreboard/components/conductor'
import i18n from 'scoreboard/components/i18n'
import scrollContainer from 'scoreboard/components/scroll-container'
import { isV5, isV6 } from 'scoreboard/components/version'

// IMPORTANT: don't try to fall back to the behavior in the regular
// DisplayHeader because it breaks animations of charts and speedometers
var OverviewHeader = ScorecardsDisplayHeader.extend({
	redrawDisplay: function(newPeriodId) {
		this.model.fetch({
			data: {
				notes: true,
				dataUsedInCalculations: true,
				chart: true,
				calendarPeriodId: globals.periodId,
				relatedItems: true
			},
			success: (model, response, options) => {
				if (!response.notes) {
					this.model.unset('notes') // #18274
				}
				this.trigger('change:period', newPeriodId)
			}
		})
	}
})

var ScorecardsOverview = Backbone.View.extend({

	// this.model is a ScorecardNode

	className: 'display-layout scorecards-overview',

	initialize: function(options = {}) {
		this.options = options
		this.fullContext = context(this.model)
		this.header = new OverviewHeader({
			model: this.model,
			subsection: 'overview',
			sendToOptions: SendToOptions.default()
				.concat(SendToOptions.scorecardStructureImport())
				.concat(SendToOptions.scorecardValueImport()),
			importOptions: ImportOptions.forScorecardItem(this.model),
			tabs: tabs({
				...this.model.attributes,
			})
		})
		this.notesLayout = new NotesLayout({ model: this.model })
		this.performance = new Performance({ model: this.model })
		this.historical = new Historical({ model: this.model })
		this.related = new Related({ model: this.model })
		if (this.fullContext.metricish) {
			this.values = new Values({ model: this.model })
			// #28518 after an metric value import completes, reload the page
			// so we have the latest data
			this.model.on('import-complete', () => {
				conductor.reload()
			})
		} else {
			this.calculations = new Calculations({ model: this.model })
		}

		this.on('resize', () => this.resize())
		// can't listen to changing scores, b/c might go from no scores to no
		// scores and we still need to re-render the chart in that case
		this.listenTo(this.header, 'change:period', this._changePeriod.bind(this))
		this.listenTo(this.model, 'change:values', this._changeScores)
		if (isV5()) {
			this.listenTo(this.related, 'change:relatedItems', this._positionSecondRow)
		}
	},

	resize() {
		this.historical.trigger('resize')
		this._positionSecondRow()
		this.notesLayout.trigger('resize')
		this.performance.trigger('resize')

		if (isV5()) {
			// If we have a description, it's placed at the top of the performance
			// panel which makes for a variable height for that panel. For that reason,
			// manually resize the historical panel to keep it in sync with the
			// performance panel height.
			if (this._hasDescription()) {
				this._resizeHistoricalPanel()
			}
		}

		this.onScroll()
	},

	onScroll() {
		const chartSvg = this.$('.chart-svg')
		if (chartSvg.length == 0) return

		// To prevent chart tooltips from appearing in front of the display header (#38456),
		// stop showing them on hover.
		let headerHeight = this.$('header').height()
		let chartTop = chartSvg[0].getBoundingClientRect().top
		chartSvg.toggleClass('is-covered', chartTop < headerHeight)
	},

	render: function () {
		this.$el.empty().append(this.notesLayout.el)
		this.notesLayout.render()
		this.notesLayout.setHeader(this.header)
		this.header.toggleRedDescendants(this.model.get('descendantLowestScore'))

		var mainContainer = this.notesLayout.mainContentContainer()
		mainContainer.empty().append(template(this.model.attributes))

		// append performance view to the DOM before rendering (speedometer needs to be in the DOM)
		mainContainer.find('.performance').empty().append(this.performance.el)
		this.performance.render()
		if (isV5()) {
			// #26981 prevent js error if no description
			if (this.performance.descriptionLineClamp) {
				this.performance.descriptionLineClamp.on('toggleExpanded', () => this._positionSecondRow())
			}
		}

		mainContainer.find('.historical').empty().append(this.historical.el)
		this.historical.render()

		// hide the notes until we figure out how they will be displayed
		this.$('.notes').hide()

		let status = this.model.get('status')
		if (status != 'weighted') {
			this.$('.banner.recalculation-needed').removeClass('is-not-displayed')
			// #36051 Change message when just percent weightings need to be
			// calculated (status is fresh (0) )
			if (status === 'fresh') {
				this.$('.banner.recalculation-needed').text(i18n.get('scorecards.overview.weightRecalculationNeeded'))
			}
		}

		if (!this.model.get('pendingDeletion')) {
			this.$('.banner.pending-deletion').remove()
		}

		if (this.values) {
			mainContainer.find('.values').empty().append(this.values.el)
			this.values.render()
		}

		mainContainer.find('.related').empty().append(this.related.el)
		this.related.render()

		if (this.calculations) {
			mainContainer.find('.calculations').empty().append(this.calculations.el)
			this.calculations.render()
		}

		this._toggleNotVisible()

		this.secure()

		this.notesLayout.displayNote({
			id: this.options.noteId,
			replyId: this.options.replyId,
			noteAction: this.options.noteAction
		})

		//for docking nodes show special message
		if (this.model.get('type') === 'docking' || this.model.get('dereferenced')) {
			let msgKey = 'common.replication.dockingNeedsTransforming'
			if (_.isNil(this.model.get('syncId'))) {
				msgKey = 'common.replication.viewingReplicated.nipr'
			}
			if (this.model.get('dereferenced')) {
				msgKey = 'common.replication.viewingDereferenced'
				if (this.model.get('type') === 'root') {
					msgKey = 'common.replication.viewingRootDereferenced'
				}
			}

			mainContainer.empty().append(cannotEdit({ messageKey: msgKey }))
			this.notesLayout.toggleVisibility(false)
		}

		if (isV5()) {
			if (this._hasDescription()) {
				this.originalHistoricalHeight = this.$('.performance').height()
				this._resizeHistoricalPanel()
			}
		}
		// #32577 If linked node, show related items but disallow
		// item creation/deletion
		if (this.model.get('type') === 'linked' && this.model.get('useSourceRelatedItems')) {
			this.related.$('.right-buttons').addClass('is-not-displayed')
		}

		// Wait until everything else has been rendered before determining
		// the correct positioning of the second row panels and the notes layout.
		this._positionSecondRow()
		this.notesLayout.resize()

		// When a scroll happens (wide + narrow screen size), handle it with onScroll()
		scrollContainer(this.el).on('scroll', () => this.onScroll())
		this.$('.display-container').on('scroll', () => this.onScroll())
		return this
	},

	_resizeHistoricalPanel: function() {
		// If we're in the medium/small display size, the panels are stacked, no need
		// to set the height of the .historical panel then, it's set in the scss
		if (layoutManager.getDisplaySize() == 'display-medium' ||
			layoutManager.getDisplaySize() == 'display-small') {
			this.$('.historical .chart-container').css('height', '')
			this.historical.trigger('resize')
			return
		}

		let height
		if (this.performance.descriptionLineClamp?.isExpanded()) {
			height = this.originalHistoricalHeight
		} else {
			height = this.$('.performance').height()
		}

		let panelHeaderHeight = this.$('.historical .panel-header').height() - 23
		this.$('.historical .chart-container').css('height', height - panelHeaderHeight)
		this.historical.trigger('resize')
	},

	secure: function() {
		// let's just worry about the edit tab here and let sub-views secure themselves
		this.header.conditionallyHideEditTab(this.model.isEditable())
	},

	// callback when the calendar period is changed
	_changePeriod: function() {
		this._changeScores()
		this._toggleNotVisible()
		this.header.toggleRedDescendants(this.model.get('descendantLowestScore'))
		this.notesLayout.trigger('resize') // #19479 - Show/Hide notes as space allows
	},

	// determines whether the scorecard item is visible based on start/archive
	// dates
	_isVisible: function() {
		var period = calendars.periodsById[globals.periodId]
		return visible({
			start: this.model.get('ancestorStart'),
			archive: this.model.get('ancestorArchive')
		}, period)
	},

	_toggleNotVisible: function() {
		var visible = this._isVisible()
		this.$('.scorecard-not-visible').toggleClass('is-not-displayed', visible)
		if (!visible) {
			this.$('.scorecard-not-visible').text(this._getNotVisibleMessage())
		}
	},

	_getNotVisibleMessage: function () {
		let archive = this.model.get('ancestorArchive')
		let start = this.model.get('ancestorStart')
		return notVisibleMessage(archive, start)
	},

	// callback when something happens that might change the scores being displayed
	_changeScores: function() {
		_.invokeMap([this.performance, this.historical, this.values, this.calculations, this.related], 'render')
	},

	// TODO: Remove when V5 is no longer supported - remove entire method
	// fix the positioning of the values or calculations panel and related items
	// panel.
	_positionSecondRow: function() {
		let firstPanel = this.$(this.values ? '.values' : '.calculations')
		let secondPanel = this.$('.related')
		let panelRow = this.$('.calc-values-related-row')

		firstPanel.find('table').css('min-width', '')
		secondPanel.find('table').css('min-width', '')
		if (isV5()) {
			// If description is expanded, we will determine how the second row panels
			// should be laid out.
			let description = this.$('.description')
			let smallPane = _.includes(['display-medium', 'display-small'],
				layoutManager.getDisplaySize())
			if (description.hasClass('is-expanded') && !smallPane) {
				let historical = this.$('.historical')
				let calcWidth = firstPanel.width()
				let historicalWidth = historical.width()

				// If calcWidth does not equal the scroll-container or historical panel
				// widths, then the calculations and related items row has a custom
				// width applied to match the historical panel width.
				if (calcWidth !== this.$('.scroll-container').width() && calcWidth !== historicalWidth) {
					panelRow.addClass('is-right-aligned')
					// Remove the fixed pixel widths previously applied before expanding
					// the description and instead have them stretch the full width
					// of the panelRow.
					firstPanel.css('width', '100%')
					secondPanel.css('width', '100%')
				}
				return
			}
		}

		/*
		 * If description is not expanded, determine the width of the the two panels
		 */

		// Remove class that panelRow may have received
		// from expanding the description.
		panelRow.removeClass('is-right-aligned')

		// Hide unused panel b/c it's padding throws off the layout
		this.$('.calculations-values').not(firstPanel).addClass('is-not-displayed')

		firstPanel.removeClass('large').removeAttr('style')
		secondPanel.removeClass('large').removeAttr('style')

		var positionLarge = function() {
			firstPanel.addClass('large').removeAttr('style')
			secondPanel.addClass('large').removeAttr('style')
		}.bind(this)
		if (!_.isNil(firstPanel.position()) && !_.isNil(secondPanel.position()) && 
			firstPanel.position().top == secondPanel.position().top) {
			var firstWidth = firstPanel.width()
			var secondWidth = secondPanel.width()
			var totalWidth = firstWidth + secondWidth

			if (isV5()) {
				firstPanel.css('width', (100 * firstWidth / totalWidth) + '%')
				secondPanel.css('width', (100 * secondWidth / totalWidth) + '%')
				// #29548
				firstPanel.find('table').css('min-width', firstPanel.width())
				secondPanel.find('table').css('min-width', secondPanel.width())

				// If the two divs fit next to one another but the related items
				// header overlaps the edit buttons, push them to different rows
				var relatedItemsOverlap = secondPanel.find('h3').outerWidth()
					>= secondPanel.find('.panel-title-container').innerWidth()

				if (relatedItemsOverlap) {
					positionLarge()
				}
			}

			if (isV6()) {
				// if panelRow is overflowing horizontally, apply the large 
				// class to the panels 
				if (panelRow[0].scrollWidth > panelRow[0].clientWidth) {
					positionLarge()
				}

			}
		} else {
			positionLarge()
		}
	},

	_hasDescription: function() {
		return !_.isEmpty(_.trim(this.model.get('description')))
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)

		_.invokeMap([this.header, this.notesLayout, this.performance, this.values,
			this.historical, this.related, this.calculations], 'remove')
	}

})

ScorecardsOverview.loadOptions = {
	relatedItems: true,
	notes: true,
	dataUsedInCalculations: true,
	chart: true,
	ancestorStartArchive: true,
	percentWeightingIgnoringPerformance: true,
	alertSubscriptions: true
}

export default ScorecardsOverview
ScorecardsOverview.__filename__ = 'scorecards/overview/scorecards-overview.js'
